// Fusiona dos trades en uno. Caso típico: el agente MT5 sincronizó un trade y
// existe un manual separado con screenshots/análisis que no se auto-mergeó.
//
// Regla: si alguno es source='mt5', ese queda (tiene la data precisa). El otro
// aporta el journal y se elimina. Si ambos son manuales, queda el del detalle
// abierto (currentTrade).

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Trade } from '@bini-trading/shared-types';
import { api } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { cn } from '../../lib/cn';

interface Props {
  open: boolean;
  currentTrade: Trade;
  allTrades: Trade[];
  onClose: () => void;
  onMerged: () => void;
}

const QTY_EPSILON = 0.001;
const WINDOW_MS = 48 * 60 * 60 * 1000;

const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('es-CL', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
const fmt = (n: number): string =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 5 }).format(n);

export function LinkTradeModal({
  open,
  currentTrade,
  allTrades,
  onClose,
  onMerged,
}: Props): ReactElement | null {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Candidatos: misma cuenta, mismo símbolo+side, qty ±0.001, entryAt ±48h.
  // Filtra el current y prioriza otra source (caso usual: linkear manual↔MT5).
  const candidates = useMemo(() => {
    const currentEntryMs = new Date(currentTrade.entryAt).getTime();
    return allTrades
      .filter(
        (t) =>
          t.id !== currentTrade.id &&
          t.accountId === currentTrade.accountId &&
          t.symbol === currentTrade.symbol &&
          t.side === currentTrade.side &&
          Math.abs(t.quantity - currentTrade.quantity) <= QTY_EPSILON &&
          Math.abs(new Date(t.entryAt).getTime() - currentEntryMs) <= WINDOW_MS,
      )
      .sort((a, b) => {
        // Source opuesto primero (manual↔mt5 es el caso esperado), después por
        // proximidad temporal.
        const aOpposite = a.source !== currentTrade.source;
        const bOpposite = b.source !== currentTrade.source;
        if (aOpposite !== bOpposite) return aOpposite ? -1 : 1;
        return (
          Math.abs(new Date(a.entryAt).getTime() - currentEntryMs) -
          Math.abs(new Date(b.entryAt).getTime() - currentEntryMs)
        );
      });
  }, [currentTrade, allTrades]);

  const selected = candidates.find((c) => c.id === selectedId) ?? null;

  // Cuál se conserva: el MT5 si alguno lo es, sino el current.
  const keepAndMerge = selected
    ? selected.source === 'mt5' && currentTrade.source !== 'mt5'
      ? { keep: selected, merge: currentTrade }
      : { keep: currentTrade, merge: selected }
    : null;

  const mergeMutation = useMutation({
    mutationFn: ({ keepId, mergeId }: { keepId: string; mergeId: string }) =>
      api(`/trades/${keepId}/merge-from/${mergeId}`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trades'] });
      qc.invalidateQueries({ queryKey: ['pending-summary'] });
      qc.invalidateQueries({ queryKey: ['analytics-calendar'] });
      onMerged();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Linkear con otro trade"
      description="Fusiona este trade con otro de la misma operación (típico: el agente MT5 trajo el trade pero tu manual con screenshots quedó separado)."
      size="lg"
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!keepAndMerge}
            loading={mergeMutation.isPending}
            onClick={() => {
              if (!keepAndMerge) return;
              mergeMutation.mutate({
                keepId: keepAndMerge.keep.id,
                mergeId: keepAndMerge.merge.id,
              });
            }}
          >
            Fusionar
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Trade actual — referencia visual */}
        <div className="rounded-lg border border-surface-border bg-surface-panel/30 p-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-subtle font-semibold mb-2">
            Trade actual
          </div>
          <TradeSummaryRow trade={currentTrade} />
        </div>

        {/* Lista de candidatos */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold mb-2">
            Candidatos compatibles · {candidates.length}
          </div>
          {candidates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-surface-border p-6 text-center text-sm text-ink-subtle">
              Ningún trade matchea (mismo símbolo, side, cantidad ±0.001, entrada
              dentro de ±48h, misma cuenta).
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {candidates.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedId(c.id)}
                  className={cn(
                    'w-full text-left rounded-lg border p-3 transition-colors',
                    selectedId === c.id
                      ? 'border-brand-500 bg-brand-500/5'
                      : 'border-surface-border bg-surface-elevated hover:border-surface-strong',
                  )}
                >
                  <TradeSummaryRow trade={c} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Preview del resultado */}
        {keepAndMerge && (
          <div className="rounded-lg border border-brand-500/30 bg-brand-500/5 p-3 text-xs text-ink-muted">
            <div className="font-semibold text-ink mb-1">Resultado al fusionar:</div>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                Conserva el trade{' '}
                <span className="font-mono text-ink">{keepAndMerge.keep.source}</span>{' '}
                de {fmtDateTime(keepAndMerge.keep.entryAt)}.
              </li>
              <li>
                Los {keepAndMerge.merge.screenshots.length} screenshot(s) y el journal
                del otro pasan a este.
              </li>
              <li>
                Campos del journal: se conservan los del trade que queda; sólo se
                copian los que estén vacíos.
              </li>
              <li>El trade origen se elimina.</li>
            </ul>
          </div>
        )}

        {mergeMutation.isError && (
          <div className="rounded-lg border border-loss-DEFAULT/40 bg-loss-DEFAULT/5 p-3 text-xs text-loss-DEFAULT">
            Error al fusionar: {(mergeMutation.error as Error).message}
          </div>
        )}
      </div>
    </Modal>
  );
}

function TradeSummaryRow({ trade }: { trade: Trade }): ReactElement {
  const pnl = trade.pnl ?? 0;
  const pnlColor =
    pnl > 0 ? 'text-win-DEFAULT' : pnl < 0 ? 'text-loss-DEFAULT' : 'text-ink-muted';
  return (
    <div className="flex items-center gap-3 flex-wrap text-sm">
      <span
        className={cn(
          'text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border',
          trade.source === 'mt5'
            ? 'border-info-DEFAULT/40 bg-info-DEFAULT/10 text-info-DEFAULT'
            : 'border-surface-border bg-surface-panel text-ink-muted',
        )}
      >
        {trade.source}
      </span>
      <span className="font-mono font-bold">{trade.symbol}</span>
      <span className="text-ink-muted">{trade.side === 'long' ? '↑ Long' : '↓ Short'}</span>
      <span className="font-mono text-ink-muted">{fmt(trade.quantity)}</span>
      <span className="font-mono text-ink-subtle">@ {fmt(trade.entryPrice)}</span>
      <span className="text-xs text-ink-subtle ml-auto">{fmtDateTime(trade.entryAt)}</span>
      {trade.pnl != null && (
        <span className={cn('font-mono font-semibold', pnlColor)}>
          {trade.pnl > 0 ? '+' : ''}
          {fmt(trade.pnl)}
        </span>
      )}
      {trade.screenshots.length > 0 && (
        <span className="text-[10px] text-ink-subtle">📷 {trade.screenshots.length}</span>
      )}
    </div>
  );
}
